/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { inject, Injectable } from '@angular/core';
import { FabLibraryInfo } from '@fabric/components/header-actions';
import { AuthConfig } from 'angular-oauth2-oidc';

import { environment } from '../../../environments/environment';
import { WindowRef } from '../window-ref.service';

export interface Environment {
  production?: boolean;
  appName?: string;
  appVersion?: string;
  appVersionCodename?: string;
  buildNumber?: number | string;
  environmentName?: string;
  libraries?: FabLibraryInfo[];
  apiMap?: Record<string, string>;
  appLauncherUrls?: Record<string, string>;
  featureToggleMap?: Record<string, boolean>;
  auth?: AuthConfig;
  backendApiUrl?: string;
}

export const DEFAULT_BACKEND_API_URL = 'http://localhost:3000';

/**
 * Resolves the backend API base URL, falling back to the local backend when none is configured.
 * @param environment - the current environment
 * @returns the backend API base URL
 */
export function resolveBackendApiUrl(environment?: Environment): string {
  return environment?.backendApiUrl || DEFAULT_BACKEND_API_URL;
}

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService {
  public environment: Environment = environment;

  private readonly winRef: WindowRef = inject(WindowRef);

  constructor() {
    this.augmentEnvironment();
  }

  /**
   * Grab environment from the window and add it to the Angular environment object.
   */
  private augmentEnvironment(): void {
    const dynamicConfig: Environment = this.winRef.nativeWindow.__env; // eslint-disable-line no-underscore-dangle
    if (dynamicConfig) {
      Object.assign(this.environment, dynamicConfig);
    }
  }
}
